import { observer } from 'mobx-react-lite'
import { Typography } from '@mui/material'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ProjectDetailsDto } from '../../model/project-builder'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'

type ProjectInsightsCardProps = {
  project?: ProjectDetailsDto
}

export const ProjectInsightsCard = observer(({
  project,
}: ProjectInsightsCardProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  if (!project) {
    return null
  }

  const gaps = project.gaps ?? []
  const now = new Date()
  const endDate = new Date(project.endDate)
  const diffInMs = endDate.getTime() - now.getTime()
  const daysRemaining = Math.ceil(
    diffInMs / (1000 * 60 * 60 * 24),
  )

  const hasGaps = gaps.length > 0

  return (
    <AppPaper sx={styles.root}>
      <Typography variant='h2' sx={styles.title}>
        {projectBuilderTranslation.projectInsights()}
      </Typography>

      {hasGaps ? (
        <>
          <Typography variant='body1' sx={styles.subtitle}>
            {projectBuilderTranslation.gapsInProject()}
          </Typography>

          <AppBox sx={styles.tagsContainer}>
            {gaps.map(gap => (
              <AppBox
                key={gap.skillName}
                sx={styles.tag}
              >
                {gap.skillName}
              </AppBox>
            ))}
          </AppBox>
        </>
      ) : (
        <>

          <Typography variant='body1' sx={styles.subtitle}>
            {daysRemaining > 0
              ? `${daysRemaining} ${projectBuilderTranslation.daysRemaining()}`
              : projectBuilderTranslation.projectEnded()}
          </Typography>
        </>
      )}

    </AppPaper>
  )
})

const styles = {
  root: {
    borderRadius: '12px',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    backgroundColor: Palette.CORAL_REEF_GREEN,
    justifyContent: 'center',
    width: '100%',
    border: 0,
  },
  title: {
    color: ColorTokens.neutral100,
  },
  subtitle: {
    marginTop: '-15px',
    color: ColorTokens.primary200,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
  },
  tag: {
    padding: '3px 7px',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    border: `1px solid ${ColorTokens.primary200}`,
    fontSize: 12,
    color: ColorTokens.neutral100,
  },
} as const